import { useState, useRef, useEffect } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useAtomValue, useSetAtom } from 'jotai'
import { currentUserQueryAtom } from '../auth/currentUser'
import { authClient } from '../auth/client'
import { resetQueryClient } from '../queryClient'
import { pageLinks } from '../pageLinks'

// ============================================================================
// Icons
// ============================================================================

function MenuIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
    </svg>
  )
}

function CloseIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
    </svg>
  )
}

function DocumentIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
    </svg>
  )
}

function ChartIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 0 1 3 19.875v-6.75ZM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V8.625ZM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V4.125Z" />
    </svg>
  )
}

function ListIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 6.75h12M8.25 12h12m-12 5.25h12M3.75 6.75h.007v.008H3.75V6.75Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0ZM3.75 12h.007v.008H3.75V12Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm-.375 5.25h.007v.008H3.75v-.008Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z" />
    </svg>
  )
}

function CogIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.325.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 0 1 1.37.49l1.296 2.247a1.125 1.125 0 0 1-.26 1.431l-1.003.827c-.293.241-.438.613-.43.992a7.723 7.723 0 0 1 0 .255c-.008.378.137.75.43.991l1.004.827c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 0 1-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.47 6.47 0 0 1-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.94-1.11.94h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 0 1-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 0 1-1.369-.49l-1.297-2.247a1.125 1.125 0 0 1 .26-1.431l1.004-.827c.292-.24.437-.613.43-.991a6.932 6.932 0 0 1 0-.255c.007-.38-.138-.751-.43-.992l-1.004-.827a1.125 1.125 0 0 1-.26-1.43l1.297-2.247a1.125 1.125 0 0 1 1.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.086.22-.128.332-.183.582-.495.644-.869l.214-1.28Z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
    </svg>
  )
}

function StorefrontIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 21v-7.5a.75.75 0 0 1 .75-.75h3a.75.75 0 0 1 .75.75V21m-4.5 0H2.36m11.14 0H18m0 0h3.64m-1.39 0V9.349M3.75 21V9.349m0 0a3.001 3.001 0 0 0 3.75-.615A2.993 2.993 0 0 0 9.75 9.75c.896 0 1.7-.393 2.25-1.016a2.993 2.993 0 0 0 2.25 1.016c.896 0 1.7-.393 2.25-1.015a3.001 3.001 0 0 0 3.75.614m-16.5 0a3.004 3.004 0 0 1-.621-4.72l1.189-1.19A1.5 1.5 0 0 1 5.378 3h13.243a1.5 1.5 0 0 1 1.06.44l1.19 1.189a3 3 0 0 1-.621 4.72M6.75 18h3.75a.75.75 0 0 0 .75-.75V13.5a.75.75 0 0 0-.75-.75H6.75a.75.75 0 0 0-.75.75v3.75c0 .414.336.75.75.75Z" />
    </svg>
  )
}

function BanknoteIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18.75a60.07 60.07 0 0 1 15.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 0 1 3 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 0 0-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 0 1-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 0 0 3 15h-.75M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm3 0h.008v.008H18V10.5Zm-12 0h.008v.008H6V10.5Z" />
    </svg>
  )
}

// ============================================================================
// Navigation
// ============================================================================

type NavKey = 'label' | 'dashboard' | 'listings' | 'amazon' | 'orders' | 'copilot' | 'settings'

function CoPilotIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09ZM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 0 0-2.455 2.456ZM16.894 20.567 16.5 21.75l-.394-1.183a2.25 2.25 0 0 0-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 0 0 1.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 0 0 1.423 1.423l1.183.394-1.183.394a2.25 2.25 0 0 0-1.423 1.423Z" />
    </svg>
  )
}

const NAV_ITEMS: { key: NavKey; label: string; href: string; icon: typeof DocumentIcon }[] = [
  { key: 'label', label: 'Label Generator', href: '/', icon: DocumentIcon },
  { key: 'listings', label: 'A+ Listings', href: '/aplus/listings', icon: ListIcon },
  // { key: 'amazon', label: 'Amazon Listings', href: '/amazon/listings', icon: StorefrontIcon },
  // { key: 'orders', label: 'Orders', href: '/amazon/orders', icon: BanknoteIcon },
  // { key: 'copilot', label: 'CoPilot', href: '/copilot', icon: CoPilotIcon },
]

function NavigationPanel({ activeNav }: { activeNav: NavKey }) {
  return (
    <div className="w-60 shrink-0 h-full border-r border-[#E5E5E5] bg-white">
      <nav className="p-2 space-y-0.5">
        {NAV_ITEMS.map((item) => {
          const isActive = item.key === activeNav
          return (
            <Link
              key={item.key}
              to={item.href}
              className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] font-medium transition-colors ${
                isActive
                  ? 'bg-[#F5F5F5] text-[#18181B]'
                  : 'text-[#71717A] hover:bg-[#FAFAFA] hover:text-[#18181B]'
              }`}
            >
              <item.icon className="size-4 text-[#71717A]" />
              {item.label}
            </Link>
          )
        })}
      </nav>
    </div>
  )
}

// ============================================================================
// Top Bar
// ============================================================================

function SparkleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 16 16" fill="currentColor">
      <path d="M8 0L9.79 6.21L16 8L9.79 9.79L8 16L6.21 9.79L0 8L6.21 6.21L8 0Z" />
    </svg>
  )
}

export function UserMenu() {
  const { data: user, isPending } = useAtomValue(currentUserQueryAtom)
  const resetQuery = useSetAtom(resetQueryClient)
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  // Close dropdown on outside click
  useEffect(() => {
    if (!open) return
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [open])

  if (isPending) return null

  // Not logged in — hide menu until auth is fully wired up
  if (!user) return null

  const initial = (user.name?.[0] || user.email[0] || '?').toUpperCase()

  return (
    <div ref={menuRef} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="size-8 rounded-full bg-[#16A34A] text-white text-xs font-semibold flex items-center justify-center cursor-pointer hover:opacity-90 transition-opacity"
        title={user.email}
      >
        {initial}
      </button>

      {open && (
        <div className="absolute right-0 top-10 w-56 bg-white border border-[#E5E5E5] rounded-lg shadow-lg py-1 z-50">
          <div className="px-3 py-2 border-b border-[#E5E5E5]">
            {user.name && <p className="text-[13px] font-medium text-[#18181B] truncate">{user.name}</p>}
            <p className="text-[12px] text-[#71717A] truncate">{user.email}</p>
          </div>
          <button
            onClick={async () => {
              setOpen(false)
              await authClient.signOut()
              resetQuery()
              navigate(pageLinks.global_shortcut_login())
            }}
            className="w-full text-left px-3 py-2 text-[13px] text-[#71717A] hover:bg-[#F5F5F5] hover:text-[#18181B] transition-colors cursor-pointer"
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  )
}

function TopBar({ menuOpen, onMenuToggle, topBarActions }: { menuOpen: boolean; onMenuToggle: () => void; topBarActions?: React.ReactNode }) {
  return (
    <div className="h-14 px-5 flex items-center justify-between border-b border-[#E5E5E5] shrink-0 bg-white">
      <div className="flex items-center gap-3">
        <button onClick={onMenuToggle} className={`p-1 -ml-1 rounded transition-colors cursor-pointer ${menuOpen ? 'bg-[#F5F5F5] text-[#18181B]' : 'hover:bg-[#F5F5F5] text-[#71717A]'}`}>
          <MenuIcon className="size-5" />
        </button>
        <div className="flex items-center gap-2">
          <div className="size-2 rounded-full bg-[#16A34A]" />
          <span className="text-sm font-semibold text-[#18181B]">Vertco</span>
          <SparkleIcon className="size-3.5 text-[#A1A1AA]" />
        </div>
      </div>
      <div className="flex items-center gap-3">
        {topBarActions}
        <UserMenu />
      </div>
    </div>
  )
}

// ============================================================================
// AppShell
// ============================================================================

function useMenuState() {
  const [menuOpen, setMenuOpen] = useState(() => sessionStorage.getItem('menuOpen') !== 'false')
  const toggle = () => {
    const next = !menuOpen
    sessionStorage.setItem('menuOpen', String(next))
    setMenuOpen(next)
  }
  return [menuOpen, toggle] as const
}

export function AppShell({ activeNav, children, topBarActions }: { activeNav: NavKey; children: React.ReactNode; topBarActions?: React.ReactNode }) {
  const [menuOpen, toggleMenu] = useMenuState()

  return (
    <div className="h-screen flex flex-col bg-white">
      <TopBar menuOpen={menuOpen} onMenuToggle={toggleMenu} topBarActions={topBarActions} />
      <div className="flex-1 flex min-h-0 overflow-hidden">
        {/* Menu panel with slide animation */}
        <div
          className="shrink-0 overflow-hidden transition-[width] duration-200 ease-out"
          style={{ width: menuOpen ? 240 : 0 }}
        >
          <NavigationPanel activeNav={activeNav} />
        </div>
        {/* Page content */}
        <div className="flex-1 flex flex-col min-w-0">
          {children}
        </div>
      </div>
    </div>
  )
}
